import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, subDays } from "date-fns";
import { Copy, Download, ExternalLink, Loader2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  hospitalAdminApi,
  type RoundRobinAssigneePayload,
  type SarvamBillingSummary,
  type SarvamCallLogLeadRef,
  type SarvamVoiceAiSettings,
} from "@/api/hospitalAdmin";
import { SarvamVoiceAiSettingsCard } from "@/components/crm/SarvamVoiceAiSettingsCard";
import { SarvamAppointmentReminderSettingsCard } from "@/components/crm/SarvamAppointmentReminderSettingsCard";
import { SarvamDnpNotReachableSettingsCard } from "@/components/crm/SarvamDnpNotReachableSettingsCard";
import { SarvamOpdLeadAssignCard } from "@/components/crm/SarvamOpdLeadAssignCard";
import { ReportHospitalFilter } from "@/components/reports/ReportHospitalFilter";
import {
  intelligenceReportHospitalId,
  useChainReportHospitalScope,
} from "@/hooks/useChainReportHospitalScope";
import { toast } from "sonner";
import { formatDateTime } from "@/utils/dateUtils";
import { CRM_LEAD_SOURCES, formatLeadSourceLabel } from "@/lib/leadSources";

function formatInr(n: number): string {
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function canRetrySarvamCall(processingStatus: string): boolean {
  const s = processingStatus.toUpperCase();
  return s === "DEAD" || s === "FAILED" || s === "QUEUED";
}

function SarvamCallLogKpiCount(props: {
  count: number;
  leads: SarvamCallLogLeadRef[] | undefined;
  onOpenLeads: (title: string, leads: SarvamCallLogLeadRef[]) => void;
  dialogTitle: string;
}) {
  const { count, leads, onOpenLeads, dialogTitle } = props;
  if (count <= 0) {
    return <span className="text-2xl font-semibold tabular-nums">0</span>;
  }
  return (
    <button
      type="button"
      className="text-2xl font-semibold tabular-nums text-primary underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
      onClick={() => onOpenLeads(dialogTitle, leads ?? [])}
    >
      {count}
    </button>
  );
}

function integrationStatusBadge(status: string) {
  if (status === "active") return <Badge>Active</Badge>;
  if (status === "configured") return <Badge variant="secondary">Not enabled</Badge>;
  if (status === "disabled") return <Badge variant="secondary">Disabled</Badge>;
  return <Badge variant="outline">Planned</Badge>;
}

export default function SarvamIntegrationsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [leadDrillDown, setLeadDrillDown] = useState<{
    title: string;
    leads: SarvamCallLogLeadRef[];
  } | null>(null);

  const openCallLogLeads = (title: string, leads: SarvamCallLogLeadRef[]) => {
    if (leads.length === 1) {
      navigate(`/hospital-admin/leads?openLeadId=${encodeURIComponent(leads[0].leadId)}`);
      return;
    }
    setLeadDrillDown({ title, leads });
  };
  const today = format(new Date(), "yyyy-MM-dd");
  const defaultFrom = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(today);
  const [voiceRate, setVoiceRate] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [agentFilter, setAgentFilter] = useState<string>("all");
  const [salesPersonFilter, setSalesPersonFilter] = useState<string>("all");
  const [leadSourceFilter, setLeadSourceFilter] = useState<string>("all");

  const {
    showHospitalFilter,
    hospitalOptions,
    selectedHospitalId,
    setSelectedHospitalId,
    isSuperAdmin,
    isChainScoped,
  } = useChainReportHospitalScope({ defaultScope: "consolidated" });

  const billingHospitalId = intelligenceReportHospitalId({
    showHospitalFilter,
    selectedHospitalId,
    isChainScoped,
  });

  const { data: settings, isLoading: settingsLoading } = useQuery<SarvamVoiceAiSettings>({
    queryKey: ["sarvam-voice-ai-settings"],
    queryFn: () => hospitalAdminApi.getSarvamVoiceAiSettings(),
  });

  const { data: rrData } = useQuery<RoundRobinAssigneePayload>({
    queryKey: ["lead-round-robin-assignees"],
    queryFn: () => hospitalAdminApi.listRoundRobinAssignees(),
  });
  const salesAssignees = rrData?.assignees ?? [];

  const billingQueryKey = ["sarvam-billing", fromDate, toDate, billingHospitalId];
  const {
    data: billing,
    isLoading: billingLoading,
    isError: billingError,
  } = useQuery<SarvamBillingSummary>({
    queryKey: billingQueryKey,
    queryFn: () =>
      hospitalAdminApi.getSarvamBillingSummary({
        from: fromDate ? `${fromDate}T00:00:00.000Z` : undefined,
        to: toDate ? `${toDate}T23:59:59.999Z` : undefined,
        hospitalId: billingHospitalId,
      }),
  });

  const { data: pricing, isLoading: pricingLoading } = useQuery({
    queryKey: ["sarvam-pricing-rates"],
    queryFn: () => hospitalAdminApi.getSarvamPricingRates(),
  });

  useEffect(() => {
    if (pricing?.effectiveVoiceRatePerMinuteInr != null) {
      setVoiceRate(String(pricing.effectiveVoiceRatePerMinuteInr));
    }
  }, [pricing?.effectiveVoiceRatePerMinuteInr]);

  const agentFilterOptions = useMemo(() => {
    const fromSettings = (settings?.agents ?? []).map((a) => ({
      id: a.sarvamAppId,
      label: a.name?.trim() || a.sarvamAppId,
    }));
    const seen = new Set<string>();
    const out: { id: string; label: string }[] = [];
    for (const a of fromSettings) {
      if (!a.id || seen.has(a.id)) continue;
      seen.add(a.id);
      out.push(a);
    }
    return out.sort((a, b) => a.label.localeCompare(b.label));
  }, [settings?.agents]);

  const callsQueryKey = [
    "sarvam-lead-calls",
    fromDate,
    toDate,
    billingHospitalId,
    agentFilter,
    salesPersonFilter,
    leadSourceFilter,
  ];
  const { data: callLogData, isLoading: callsLoading } = useQuery({
    queryKey: callsQueryKey,
    queryFn: () =>
      hospitalAdminApi.listSarvamLeadCalls({
        from: fromDate ? `${fromDate}T00:00:00.000Z` : undefined,
        to: toDate ? `${toDate}T23:59:59.999Z` : undefined,
        hospitalId: billingHospitalId,
        sarvamAppId: agentFilter !== "all" ? agentFilter : undefined,
        assignedToId: salesPersonFilter !== "all" ? salesPersonFilter : undefined,
        leadSource: leadSourceFilter !== "all" ? leadSourceFilter : undefined,
        limit: 500,
      }),
  });
  const leadCalls = callLogData?.calls ?? [];
  const callLogSummary = callLogData?.summary;

  const [csvDownloading, setCsvDownloading] = useState(false);

  const downloadCallsCsv = async () => {
    setCsvDownloading(true);
    try {
      const res = await hospitalAdminApi.downloadSarvamLeadCallsCsv({
        from: fromDate ? `${fromDate}T00:00:00.000Z` : undefined,
        to: toDate ? `${toDate}T23:59:59.999Z` : undefined,
        hospitalId: billingHospitalId,
        sarvamAppId: agentFilter !== "all" ? agentFilter : undefined,
        assignedToId: salesPersonFilter !== "all" ? salesPersonFilter : undefined,
        leadSource: leadSourceFilter !== "all" ? leadSourceFilter : undefined,
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `sarvam-calls-${fromDate || "all"}-to-${toDate || "all"}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download CSV");
    } finally {
      setCsvDownloading(false);
    }
  };

  const retryCallMutation = useMutation({
    mutationFn: (callId: string) => hospitalAdminApi.retrySarvamLeadCall(callId),
    onSuccess: async () => {
      toast.success("Call queued for retry");
      await qc.invalidateQueries({ queryKey: callsQueryKey });
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        "Retry failed";
      toast.error(message);
    },
  });

  const saveRateMutation = useMutation({
    mutationFn: () =>
      hospitalAdminApi.saveSarvamPricingRates({
        voiceRatePerMinuteInr: Number(voiceRate),
      }),
    onSuccess: async () => {
      toast.success("Voice rate updated");
      await qc.invalidateQueries({ queryKey: ["sarvam-pricing-rates"] });
      await qc.invalidateQueries({ queryKey: billingQueryKey });
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        "Failed to save rate";
      toast.error(message);
    },
  });

  const integrations = useMemo(
    () =>
      settings?.integrations ?? [
        {
          id: "voice_outbound_crm",
          name: "Voice AI — Outbound CRM",
          description: "Instant outbound calls on new CRM leads.",
          status: "active" as const,
        },
      ],
    [settings?.integrations]
  );

  const copyWebhook = async () => {
    const url = settings?.webhookUrl;
    if (!url) return;
    await navigator.clipboard.writeText(url);
    toast.success("Webhook URL copied");
  };

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-6xl">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Sarvam integrations</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Configure Sarvam Voice AI and track estimated usage across your hospital chain.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="voice">Voice outbound</TabsTrigger>
          <TabsTrigger value="reminder">Appointment reminder</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
          <TabsTrigger value="calls">Call log</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Integration</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold">
                  {settingsLoading ? "…" : settings?.enabled ? "ON" : "OFF"}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Rollout: {settings?.rolloutMode === "LIVE" ? "Live" : "Test"}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Voice agent</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold">
                  {settingsLoading ? "…" : settings?.agentEnabled ? "ON" : "Paused"}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {settings?.hasApiKey ? "API key configured" : "API key not set"}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Est. rate</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold">
                  {pricingLoading ? "…" : formatInr(pricing?.effectiveVoiceRatePerMinuteInr ?? 8)}
                  <span className="text-sm font-normal text-muted-foreground"> / min</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">For billing estimates only</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Available integrations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {integrations.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border p-3"
                >
                  <div>
                    <div className="font-medium">{item.name}</div>
                    <div className="text-sm text-muted-foreground">{item.description}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {integrationStatusBadge(item.status)}
                    {item.id === "voice_outbound_crm" && item.status === "active" ? (
                      <Button variant="outline" size="sm" onClick={() => setActiveTab("voice")}>
                        Configure
                      </Button>
                    ) : null}
                    {item.id === "voice_appointment_reminder" ? (
                      <Button variant="outline" size="sm" onClick={() => setActiveTab("reminder")}>
                        Configure
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Webhook URL</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Register this URL in your Sarvam agent workspace for call completion events.
              </p>
              <div className="flex gap-2">
                <Input readOnly value={settings?.webhookUrl ?? ""} className="font-mono text-xs" />
                <Button type="button" variant="outline" size="icon" onClick={copyWebhook} disabled={!settings?.webhookUrl}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          <SarvamOpdLeadAssignCard
            settings={settings}
            isLoading={settingsLoading}
            salesAssignees={salesAssignees}
          />

          <Card>
            <CardHeader>
              <CardTitle>Mini CRM</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                Inbound round-robin, WhatsApp numbers, and agency API keys remain on the Mini CRM
                settings page.
              </p>
              <Button variant="outline" asChild>
                <Link to="/hospital-admin/mini-crm-settings">
                  Open Mini CRM settings
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="voice" id="voice" className="space-y-4">
          <SarvamVoiceAiSettingsCard salesAssignees={salesAssignees} />
          <SarvamDnpNotReachableSettingsCard settings={settings} isLoading={settingsLoading} />
        </TabsContent>

        <TabsContent value="reminder" id="reminder">
          <SarvamAppointmentReminderSettingsCard settings={settings} isLoading={settingsLoading} />
        </TabsContent>

        <TabsContent value="billing" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Filters</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-4 items-end">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">From</Label>
                <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">To</Label>
                <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </div>
              {showHospitalFilter ? (
                <ReportHospitalFilter
                  hospitals={hospitalOptions}
                  value={selectedHospitalId}
                  onChange={setSelectedHospitalId}
                  isSuperAdmin={isSuperAdmin}
                />
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Estimated voice rate</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3 items-end">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">₹ per minute (outbound voice)</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={voiceRate}
                  onChange={(e) => setVoiceRate(e.target.value)}
                  className="w-40"
                />
              </div>
              <Button
                onClick={() => saveRateMutation.mutate()}
                disabled={saveRateMutation.isPending || pricingLoading}
              >
                {saveRateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save rate"}
              </Button>
              <p className="text-xs text-muted-foreground w-full">
                Used to estimate call costs in THMS. This is not your official Sarvam invoice.
              </p>
            </CardContent>
          </Card>

          {billingLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : billingError ? (
            <p className="text-sm text-destructive py-6 text-center">
              Could not load billing summary. Try again or pick a single hospital.
            </p>
          ) : billing ? (
            <>
              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Total estimated</CardTitle>
                  </CardHeader>
                  <CardContent className="text-2xl font-semibold">{formatInr(billing.totalCostInr)}</CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Calls</CardTitle>
                  </CardHeader>
                  <CardContent className="text-2xl font-semibold">
                    {billing.callCount}
                    <span className="text-sm font-normal text-muted-foreground ml-2">
                      ({billing.completedCallCount} completed)
                    </span>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Talk time</CardTitle>
                  </CardHeader>
                  <CardContent className="text-2xl font-semibold">
                    {formatDuration(billing.totalDurationSeconds)}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Avg / call</CardTitle>
                  </CardHeader>
                  <CardContent className="text-2xl font-semibold">
                    {billing.avgCostPerCallInr != null ? formatInr(billing.avgCostPerCallInr) : "—"}
                  </CardContent>
                </Card>
              </div>

              <p className="text-xs text-muted-foreground">{billing.note}</p>

              <div className="grid gap-4 lg:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle>By hospital</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Hospital</TableHead>
                          <TableHead className="text-right">Calls</TableHead>
                          <TableHead className="text-right">Est. cost</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {billing.byHospital.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} className="text-muted-foreground">
                              No calls in this period
                            </TableCell>
                          </TableRow>
                        ) : (
                          billing.byHospital.map((row) => (
                            <TableRow key={row.hospitalId}>
                              <TableCell>{row.hospitalName}</TableCell>
                              <TableCell className="text-right">{row.callCount}</TableCell>
                              <TableCell className="text-right">{formatInr(row.totalCostInr)}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>By day</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">Calls</TableHead>
                          <TableHead className="text-right">Est. cost</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {billing.byDay.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} className="text-muted-foreground">
                              No calls in this period
                            </TableCell>
                          </TableRow>
                        ) : (
                          billing.byDay.map((row) => (
                            <TableRow key={row.date}>
                              <TableCell>{row.date}</TableCell>
                              <TableCell className="text-right">{row.callCount}</TableCell>
                              <TableCell className="text-right">{formatInr(row.totalCostInr)}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            </>
          ) : null}
        </TabsContent>

        <TabsContent value="calls" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Filters</CardTitle>
              <p className="text-sm text-muted-foreground font-normal">
                <strong>From</strong> / <strong>To</strong> set the date range for call log, day-wise
                outcomes, totals in the selected period, and CSV export (defaults: last 30 days through
                today).
              </p>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-4 items-end">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">From</Label>
                <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">To</Label>
                <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </div>
              {showHospitalFilter ? (
                <ReportHospitalFilter
                  hospitals={hospitalOptions}
                  value={selectedHospitalId}
                  onChange={setSelectedHospitalId}
                  isSuperAdmin={isSuperAdmin}
                />
              ) : null}
              <div className="space-y-1 min-w-[200px]">
                <Label className="text-xs text-muted-foreground">Agent</Label>
                <Select value={agentFilter} onValueChange={setAgentFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All agents" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All agents</SelectItem>
                    {agentFilterOptions.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 min-w-[200px]">
                <Label className="text-xs text-muted-foreground">Sales person</Label>
                <Select value={salesPersonFilter} onValueChange={setSalesPersonFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All sales people" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sales people</SelectItem>
                    {salesAssignees.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 min-w-[200px]">
                <Label className="text-xs text-muted-foreground">Lead source</Label>
                <Select value={leadSourceFilter} onValueChange={setLeadSourceFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All sources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sources</SelectItem>
                    {CRM_LEAD_SOURCES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Day-wise outcomes (IST)</CardTitle>
              <p className="text-sm text-muted-foreground font-normal">
                CRM New Lead Agent: OPD booked on the call. Appointment Reminder Agent: patient confirmed
                the original slot (excludes declined or rescheduled). DNP Not Reachable agent: not-reachable
                leads called and re-engaged (answered / CRM updated off not-reachable, includes OPD booked).
                OPD visited = OPD consultation bill generated. Click a total to open the lead (or pick from
                the list when there are several). The table lists <strong>IST days that had at least one
                Sarvam call</strong> in the filter range — not every calendar day. Empty days are omitted.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">CRM — OPD booked (total)</p>
                  <SarvamCallLogKpiCount
                    count={callLogSummary?.totals.crmOpdBooked ?? 0}
                    leads={callLogSummary?.drillDown?.crmOpdBooked}
                    dialogTitle="CRM — OPD booked"
                    onOpenLeads={openCallLogLeads}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    of {callLogSummary?.totals.crmCallsTotal ?? 0} CRM calls
                  </p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">Reminder — confirmed (total)</p>
                  <SarvamCallLogKpiCount
                    count={callLogSummary?.totals.reminderConfirmed ?? 0}
                    leads={callLogSummary?.drillDown?.reminderConfirmed}
                    dialogTitle="Reminder — confirmed"
                    onOpenLeads={openCallLogLeads}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    of {callLogSummary?.totals.reminderCallsTotal ?? 0} reminder calls
                  </p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">OPD visited — reminder not confirmed</p>
                  <SarvamCallLogKpiCount
                    count={callLogSummary?.totals.opdVisitedReminderNotConfirmed ?? 0}
                    leads={callLogSummary?.drillDown?.opdVisitedReminderNotConfirmed}
                    dialogTitle="OPD visited — Agent 2 did not confirm"
                    onOpenLeads={openCallLogLeads}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    CRM booked OPD, bill generated, no reminder confirmation
                  </p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">OPD visited — reminder confirmed</p>
                  <SarvamCallLogKpiCount
                    count={callLogSummary?.totals.opdVisitedReminderConfirmed ?? 0}
                    leads={callLogSummary?.drillDown?.opdVisitedReminderConfirmed}
                    dialogTitle="OPD visited — Agent 2 confirmed"
                    onOpenLeads={openCallLogLeads}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    CRM booked OPD, bill generated, reminder confirmed slot
                  </p>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">DNP — activated (selected period)</p>
                  <SarvamCallLogKpiCount
                    count={callLogSummary?.totals.dnpActivated ?? 0}
                    leads={callLogSummary?.drillDown?.dnpActivated}
                    dialogTitle="DNP — activated leads (period)"
                    onOpenLeads={openCallLogLeads}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Distinct leads · {callLogSummary?.totals.dnpCallsTotal ?? 0} DNP calls in range
                  </p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">DNP — activated (all time)</p>
                  <SarvamCallLogKpiCount
                    count={callLogSummary?.totals.dnpActivatedAllTime ?? 0}
                    leads={callLogSummary?.drillDown?.dnpActivatedAllTime}
                    dialogTitle="DNP — activated leads (all time)"
                    onOpenLeads={openCallLogLeads}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Distinct leads on completed DNP calls (hospital filter only)
                  </p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">DNP — OPD booked (selected period)</p>
                  <SarvamCallLogKpiCount
                    count={callLogSummary?.totals.dnpOpdBooked ?? 0}
                    leads={callLogSummary?.drillDown?.dnpOpdBooked}
                    dialogTitle="DNP — OPD booked (period)"
                    onOpenLeads={openCallLogLeads}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Distinct leads · OPD booked on the DNP Sarvam call
                  </p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">DNP — OPD booked (all time)</p>
                  <SarvamCallLogKpiCount
                    count={callLogSummary?.totals.dnpOpdBookedAllTime ?? 0}
                    leads={callLogSummary?.drillDown?.dnpOpdBookedAllTime}
                    dialogTitle="DNP — OPD booked (all time)"
                    onOpenLeads={openCallLogLeads}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Distinct leads · completed DNP calls (hospital filter only)
                  </p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">DNP — Lost (all time)</p>
                  <SarvamCallLogKpiCount
                    count={callLogSummary?.totals.dnpLostAllTime ?? 0}
                    leads={callLogSummary?.drillDown?.dnpLostAllTime}
                    dialogTitle="DNP — Lost (9/9 not reachable)"
                    onOpenLeads={openCallLogLeads}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Leads auto-set to Lost · No response after 9 DNP attempts (respects sales person /
                    source filters)
                  </p>
                </div>
              </div>
              <Dialog
                open={leadDrillDown != null}
                onOpenChange={(open) => {
                  if (!open) setLeadDrillDown(null);
                }}
              >
                <DialogContent className="max-w-md max-h-[min(70vh,32rem)] flex flex-col">
                  <DialogHeader>
                    <DialogTitle>{leadDrillDown?.title ?? "Leads"}</DialogTitle>
                  </DialogHeader>
                  <ul className="overflow-y-auto space-y-2 pr-1 -mr-1">
                    {(leadDrillDown?.leads ?? []).map((lead, idx) => (
                      <li key={`${lead.leadId}-${idx}`}>
                        <Link
                          to={`/hospital-admin/leads?openLeadId=${encodeURIComponent(lead.leadId)}`}
                          className="flex flex-col rounded-md border px-3 py-2 hover:bg-muted/60 transition-colors"
                          onClick={() => setLeadDrillDown(null)}
                        >
                          <span className="font-medium">{lead.patientName?.trim() || "Lead"}</span>
                          {lead.phone ? (
                            <span className="text-xs text-muted-foreground">{lead.phone}</span>
                          ) : null}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </DialogContent>
              </Dialog>
              <div className="overflow-x-auto rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date (IST)</TableHead>
                      <TableHead className="text-right">CRM OPD booked</TableHead>
                      <TableHead className="text-right">CRM calls</TableHead>
                      <TableHead className="text-right">Reminder confirmed</TableHead>
                      <TableHead className="text-right">Reminder calls</TableHead>
                      <TableHead className="text-right">DNP activated</TableHead>
                      <TableHead className="text-right">DNP OPD booked</TableHead>
                      <TableHead className="text-right">DNP calls</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(callLogSummary?.byDay ?? []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-muted-foreground">
                          No calls in this period for the selected filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      (callLogSummary?.byDay ?? []).map((row) => (
                        <TableRow key={row.date}>
                          <TableCell>{row.date}</TableCell>
                          <TableCell className="text-right font-medium">{row.crmOpdBooked}</TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {row.crmCallsTotal}
                          </TableCell>
                          <TableCell className="text-right font-medium">{row.reminderConfirmed}</TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {row.reminderCallsTotal}
                          </TableCell>
                          <TableCell className="text-right font-medium">{row.dnpActivated}</TableCell>
                          <TableCell className="text-right font-medium">{row.dnpOpdBooked}</TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {row.dnpCallsTotal}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
              <CardTitle>Recent Sarvam calls</CardTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={csvDownloading || callsLoading}
                onClick={() => void downloadCallsCsv()}
              >
                {csvDownloading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Download CSV
              </Button>
            </CardHeader>
            <CardContent>
              {callsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>When</TableHead>
                        <TableHead>Lead</TableHead>
                        <TableHead>Lead source</TableHead>
                        <TableHead>Agent</TableHead>
                        <TableHead className="min-w-[14rem]">Call summary</TableHead>
                        <TableHead>Hospital</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Duration</TableHead>
                        <TableHead className="text-right">Est. cost</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leadCalls.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} className="text-muted-foreground">
                            No calls in this period
                          </TableCell>
                        </TableRow>
                      ) : (
                        leadCalls.map((call) => (
                          <TableRow key={call.id}>
                            <TableCell className="whitespace-nowrap text-xs">
                              {formatDateTime(call.completedAt ?? call.createdAt)}
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">{call.lead?.patientName ?? "—"}</div>
                              <div className="text-xs text-muted-foreground">{call.lead?.phone ?? ""}</div>
                            </TableCell>
                            <TableCell className="text-xs whitespace-nowrap">
                              {call.leadSourceLabel ||
                                (call.leadSource ? formatLeadSourceLabel(call.leadSource) : "—")}
                            </TableCell>
                            <TableCell className="text-xs max-w-[10rem]">
                              <div className="font-medium">{call.agentLabel ?? "—"}</div>
                              {call.campaignLabel ? (
                                <div className="text-muted-foreground">{call.campaignLabel}</div>
                              ) : null}
                            </TableCell>
                            <TableCell className="text-xs max-w-[18rem]">
                              {call.callSummary ? (
                                <p className="line-clamp-3" title={call.callSummary}>
                                  {call.callSummary}
                                </p>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                              {call.crmEffectSummary ? (
                                <p className="mt-1 font-medium text-emerald-900" title={call.crmEffectSummary}>
                                  CRM: {call.crmEffectSummary}
                                </p>
                              ) : null}
                              {call.appointmentReminderDetail ? (
                                <p className="mt-1 text-amber-900" title={call.appointmentReminderDetail}>
                                  {call.appointmentReminderDetail}
                                </p>
                              ) : null}
                            </TableCell>
                            <TableCell>{call.hospital?.name ?? "—"}</TableCell>
                            <TableCell className="min-w-[12rem] max-w-[20rem] align-top">
                              <div className="text-xs">{call.processingStatus}</div>
                              {call.sarvamCallStatus ? (
                                <div className="text-xs text-muted-foreground">{call.sarvamCallStatus}</div>
                              ) : null}
                              {call.lastErrorMessage ? (
                                <p
                                  className="mt-1 text-xs text-destructive whitespace-normal break-words leading-snug"
                                  title={call.lastErrorMessage}
                                >
                                  {call.lastErrorMessage}
                                </p>
                              ) : null}
                            </TableCell>
                            <TableCell className="text-right">{formatDuration(call.durationSeconds)}</TableCell>
                            <TableCell className="text-right">
                              {call.estimatedCostInr != null ? formatInr(Number(call.estimatedCostInr)) : "—"}
                            </TableCell>
                            <TableCell className="text-right">
                              {canRetrySarvamCall(call.processingStatus) ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  disabled={retryCallMutation.isPending}
                                  onClick={() => retryCallMutation.mutate(call.id)}
                                >
                                  Retry
                                </Button>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
